import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { useAuth } from '@/app/auth/context';
import { Notice } from '@/components/admin/Notice';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/admin/states';
import {
  createSuccessRecord,
  fetchSuccessRecords,
  VISIBILITY_SCOPE_LABELS,
  type SuccessVisibilityScope,
} from '@/features/success/api';
import { EventDriveFilesBox } from '@/features/events/EventDriveFilesBox';
import { ApiError } from '@/lib/api/http';
import { dateInputToIso, toDateInputValue } from '@/lib/datetime';

/**
 * サクセス面談イベントの「面談記録」タブ（D-182）.
 *
 * 議事録の独立ページ（/success-records。会員を毎回プルダウンで選ぶ方式）を廃止し、
 * **その面談の画面で、その受講生の記録を読み書きする**形にした（実機フィードバック
 * 「サクセス面談に対して議事録というかサクセス面談の内容書くと思うし」）。
 * 記録は従来どおり受講生に紐づく（`SuccessRecord`）ため、同じ受講生の過去の記録も
 * ここに並ぶ。閲覧はサクセス担当・事務局のみ（公開範囲は record 単位 / M10）。
 */
export function EventMeetingRecordsTab({
  eventId,
  memberId,
  memberName,
  eventStartsAt,
}: {
  eventId: string;
  memberId: string;
  memberName: string | null;
  eventStartsAt: string | null;
}): ReactElement {
  const { user } = useAuth();
  const canWrite = user?.active_role === 'success';
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['member-success-records', memberId],
    queryFn: () => fetchSuccessRecords(memberId),
  });

  const [minutesBody, setMinutesBody] = useState('');
  // 実施日はイベントの開催日を初期値にする（日程調整中なら空のまま入力してもらう）
  const [occurredAt, setOccurredAt] = useState(
    eventStartsAt ? toDateInputValue(eventStartsAt) : '',
  );
  const [visibilityScope, setVisibilityScope] =
    useState<SuccessVisibilityScope>('success_and_office');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onCreate(): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await createSuccessRecord({
        member_id: memberId,
        occurred_at: dateInputToIso(occurredAt),
        minutes_body: minutesBody,
        visibility_scope: visibilityScope,
      });
      await queryClient.invalidateQueries({ queryKey: ['member-success-records', memberId] });
      setMinutesBody('');
      setNotice('面談記録を登録しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <EventDriveFilesBox eventId={eventId} meetingMode />
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}

      {canWrite && (
        <div className="rounded-sm border border-wpborder p-3">
          <h3 className="mb-2 font-semibold text-wptext-strong">
            面談記録を書く{memberName ? `（${memberName} さん）` : ''}
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-wptext-muted">
                実施日<span className="text-wpnotice-error"> *</span>
              </span>
              <input
                type="date"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
                className="w-full rounded-sm border border-wpborder px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-wptext-muted">公開範囲</span>
              <select
                value={visibilityScope}
                onChange={(e) => setVisibilityScope(e.target.value as SuccessVisibilityScope)}
                className="w-full rounded-sm border border-wpborder px-3 py-2"
              >
                {Object.entries(VISIBILITY_SCOPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1.5 block text-wptext-muted">
                面談の内容<span className="text-wpnotice-error"> *</span>
              </span>
              <textarea
                rows={6}
                value={minutesBody}
                onChange={(e) => setMinutesBody(e.target.value)}
                className="w-full rounded-sm border border-wpborder px-3 py-2"
              />
            </label>
          </div>
          <button
            type="button"
            disabled={busy || !occurredAt || !minutesBody.trim()}
            onClick={() => void onCreate()}
            className="mt-3 rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {busy ? '登録中…' : '登録する'}
          </button>
        </div>
      )}

      <div>
        <h3 className="mb-2 font-semibold text-wptext-strong">
          この受講生の面談記録{memberName ? `（${memberName} さん）` : ''}
        </h3>
        {query.isPending ? (
          <SkeletonRows rows={3} columns={2} />
        ) : query.isError ? (
          <ErrorState message="面談記録を読み込めませんでした。" onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState
            title="面談記録はまだありません"
            description={canWrite ? '面談を実施したら、上のフォームから記録してください。' : undefined}
          />
        ) : (
          <ul className="space-y-3">
            {query.data.map((record) => (
              <li key={record.id} className="rounded-sm border border-wpborder p-3">
                <p className="mb-1 text-wptext-muted">
                  {new Date(record.occurred_at).toLocaleDateString('ja-JP')} /{' '}
                  {VISIBILITY_SCOPE_LABELS[record.visibility_scope]}
                </p>
                <p className="whitespace-pre-wrap">{record.minutes_body}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
